import type {
  AddCompanyResponse,
  DraftMessageInput,
  DraftMessageResponse,
  DismissResponse,
  ExpandCompaniesInput,
  ExpandCompaniesResponse,
  OutreachInput,
  OutreachResponse,
  PathwayGraphQuery,
  PathwayGraphResponse,
  PathwaysApi,
  PinResponse,
  ProposalsResponse,
  TargetCompanyDto,
} from "@/features/pathways/contracts";
import { ApiError, httpClient } from "@/shared/api/httpClient";

function qsFromGraphQuery(q?: PathwayGraphQuery): string {
  if (!q) return "";
  const params = new URLSearchParams();
  if (q.role) params.set("role", q.role);
  if (q.company) params.set("company", q.company);
  if (q.minPathStrength !== undefined) params.set("minPathStrength", String(q.minPathStrength));
  const s = params.toString();
  return s ? `?${s}` : "";
}

function emptyProposalsResponse(): ProposalsResponse {
  return {
    proposals: [],
    skippedReason: null,
    summary: {
      total: 0,
      reachableCount: 0,
      noPathCount: 0,
      criteriaVersion: 0,
    },
  };
}

export const realPathwaysApi: PathwaysApi = {
  // PATHWAY_V1-gated on the backend (guide §9). 404 → null so the UI can
  // render an empty/intro state rather than a hard error.
  async getGraph(query?: PathwayGraphQuery): Promise<PathwayGraphResponse | null> {
    try {
      return await httpClient.get<PathwayGraphResponse>(`/api/pathways/graph${qsFromGraphQuery(query)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  // Not PATHWAY_V1-gated; works in demo + authed. Deterministic fallback on
  // backend AI failure happens server-side (still HTTP 200).
  async expandCompanies(input: ExpandCompaniesInput): Promise<ExpandCompaniesResponse> {
    return httpClient.post<ExpandCompaniesResponse>(
      "/api/targets/expand-companies",
      input as unknown as Record<string, unknown>,
    );
  },

  // PATHWAY_V1-gated. 404 → empty list.
  async getProposals(): Promise<ProposalsResponse> {
    try {
      return await httpClient.get<ProposalsResponse>("/api/targets/proposals");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return emptyProposalsResponse();
      throw err;
    }
  },

  async refreshProposals(): Promise<ProposalsResponse> {
    try {
      return await httpClient.post<ProposalsResponse>("/api/targets/proposals/refresh", {});
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return emptyProposalsResponse();
      throw err;
    }
  },

  async addCompany(companyName: string): Promise<AddCompanyResponse> {
    return httpClient.post<AddCompanyResponse>("/api/targets", { companyName });
  },

  async removeCompany(targetId: string): Promise<PinResponse> {
    return httpClient.post<PinResponse>(`/api/targets/${encodeURIComponent(targetId)}/remove`, {});
  },

  async getTargetCompanies(): Promise<TargetCompanyDto[]> {
    return httpClient.get<TargetCompanyDto[]>("/api/targets/companies");
  },

  async pinProposal(id: string): Promise<PinResponse> {
    return httpClient.post<PinResponse>(`/api/targets/${encodeURIComponent(id)}/pin`, {});
  },

  async dismissProposal(id: string): Promise<DismissResponse> {
    return httpClient.post<DismissResponse>(`/api/targets/${encodeURIComponent(id)}/dismiss`, {});
  },

  async draftMessage(input: DraftMessageInput): Promise<DraftMessageResponse> {
    return httpClient.post<DraftMessageResponse>(
      "/api/pathways/draft-message",
      input as unknown as Record<string, unknown>,
    );
  },

  async recordOutreach(input: OutreachInput): Promise<OutreachResponse> {
    return httpClient.post<OutreachResponse>(
      "/api/outreach",
      input as unknown as Record<string, unknown>,
    );
  },
};
